<template>
  <header class="workspace-topbar" :class="{ 'workspace-topbar--mobile': props.mobile }">
    <a href="https://huiben.yunzhan365.com" class="mobile-logo" aria-label="返回官网首页">
      <SvgIcon name="workspace-mobile-logo" class="mobile-logo__icon" />
    </a>

    <div class="topbar-actions">
      <button v-if="canReceiveCoinGift" type="button" class="bonus-button" @click="handleClaimCoin">
        <img :src="workspaceCloudGiftIcon" class="bonus-button__icon" alt="" aria-hidden="true" />
        <span>免费领500云币</span>
      </button>

      <button
        v-if="showUpgradeButton"
        type="button"
        class="upgrade-button"
        aria-label="升级会员"
        @click="showUpgrade"
      >
        <SvgIcon name="workspace-vip" :size="24" class="upgrade-button__desktop-icon" />
        <SvgIcon name="workspace-mobile-vip" :size="36" class="upgrade-button__mobile-icon" />
        <span>升级会员</span>
      </button>

      <button type="button" class="credits-button" @click="handleBuyCoin">
        <span class="credits-button__balance">
          <SvgIcon name="ai-cost" :size="12" />
          <span>{{ balanceText }}</span>
        </span>
        <span class="credits-button__buy">购买</span>
      </button>

      <button
        ref="triggerRef"
        type="button"
        class="avatar-button"
        aria-label="打开用户菜单"
      ></button>

      <button
        type="button"
        class="workspace-menu-button"
        aria-label="打开工作台侧边栏"
        @click="handleOpenSidebar"
      >
        <SvgIcon name="workspace-menu" :size="16" />
      </button>
    </div>
  </header>

  <Teleport to="body">
    <button
      v-if="props.mobile && canReceiveCoinGift"
      type="button"
      class="mobile-bonus-button"
      @click="handleClaimCoin"
    >
      <img
        :src="workspaceMobileCloudCoinIcon"
        class="mobile-bonus-button__icon"
        alt=""
        aria-hidden="true"
      />
      <span>领500云币</span>
    </button>
  </Teleport>
</template>

<script setup lang="ts">
  import { computed, onMounted } from 'vue';
  import { storeToRefs } from 'pinia';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';
  import workspaceCloudGiftIcon from '@/assets/icons/workspace-cloud-gift.png';
  import workspaceMobileCloudCoinIcon from '@/assets/icons/workspace-mobile-cloud-coin.png';
  import { useCoinGift, useUserPanelPopover, useUpgradeModal } from '@/hooks';
  import {
    markDashboardDialogsReady,
    resetDashboardDialogs
  } from '@/hooks/useDashboardDialogCoordinator';
  import { useBalanceStore, useUserStore } from '@/store';

  interface Props {
    mobile?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    mobile: false
  });
  const userStore = useUserStore();
  const balanceStore = useBalanceStore();
  const { balance } = storeToRefs(balanceStore);
  const emit = defineEmits<{
    'open-sidebar': [];
  }>();
  const balanceText = computed(() => balance.value.toLocaleString('zh-CN'));
  const showUpgradeButton = computed(() => userStore.isLogin && userStore.isFreeUser);

  const { canReceiveCoinGift, openCoinGiftDialog, checkAndOpenCoinGift } = useCoinGift();

  function handleClaimCoin() {
    openCoinGiftDialog(true);
  }

  const { showUpgrade, showCoinBuy: handleBuyCoin } = useUpgradeModal();

  async function loadBalance() {
    await balanceStore.refreshBalance();
  }

  function handleOpenSidebar() {
    emit('open-sidebar');
  }

  const { triggerRef } = useUserPanelPopover();
  void triggerRef;

  onMounted(() => {
    resetDashboardDialogs();
    void loadBalance();
    void checkAndOpenCoinGift().then((opened) => {
      if (!opened) markDashboardDialogsReady();
    });
  });
</script>

<style scoped>
  .workspace-topbar {
    width: 100%;
    height: var(--workspace-topbar-height, 68px);
    padding: 16px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    background: transparent;
    backdrop-filter: blur(4px);
  }

  .mobile-logo {
    display: none;
  }

  .topbar-actions {
    height: 36px;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .bonus-button,
  .mobile-bonus-button,
  .upgrade-button,
  .credits-button,
  .avatar-button,
  .workspace-menu-button {
    border: 0;
    font: inherit;
    cursor: pointer;
    flex-shrink: 0;
  }

  .bonus-button {
    min-width: 142px;
    height: 36px;
    padding: 0 12px;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    border: 1px solid #eff0f7;
    border-radius: 66px;
    background: #ffffff;
    color: #d38743;
    font-family: 'Source Han Sans CN VF', 'Microsoft YaHei', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    white-space: nowrap;
  }

  .bonus-button__icon {
    width: 20px;
    height: 20px;
    display: block;
    flex-shrink: 0;
    object-fit: contain;
  }

  .mobile-bonus-button {
    display: none;
  }

  .upgrade-button {
    width: 116px;
    height: 36px;
    padding: 8px 12px;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    border: 1px solid rgba(203, 124, 30, 0.07);
    border-radius: 66px;
    background: linear-gradient(180deg, #ffedc8 0%, #ffe2a6 100%);
    color: #d38743;
    font-family: 'Source Han Sans CN VF', 'Microsoft YaHei', sans-serif;
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
    white-space: nowrap;
  }

  .upgrade-button span {
    flex-shrink: 0;
  }

  .upgrade-button__mobile-icon {
    display: none;
  }

  .credits-button {
    min-width: 110px;
    height: 36px;
    padding: 0 8px 0 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    border: 1px solid #eff0f7;
    border-radius: 66px;
    background: #ffffff;
    color: #160211;
    font-family: 'Source Han Sans CN VF', 'Microsoft YaHei', sans-serif;
    font-size: 13px;
    font-weight: 500;
    line-height: 14px;
  }

  .credits-button__balance {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    min-width: 46px;
  }

  .credits-button__buy {
    height: 22px;
    padding: 0 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 66px;
    background:
      linear-gradient(102.88deg, #e349b5 0%, #b84fff 43.75%, #834eee 89.9%),
      linear-gradient(6.88deg, #864eff -16.69%, #1473e6 115.42%);
    color: #ffffff;
    font-size: 12px;
    font-weight: 400;
    line-height: 22px;
    white-space: nowrap;
  }

  .avatar-button {
    width: 36px;
    height: 36px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: transparent;
  }

  .workspace-menu-button {
    display: none;
  }

  .workspace-topbar--mobile {
    height: 68px;
    justify-content: space-between;
    background: rgba(255, 255, 255, 0.8);
  }

  .workspace-topbar--mobile .mobile-logo {
    width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    color: #160211;
    text-decoration: none;
    overflow: hidden;
  }

  .workspace-topbar--mobile .mobile-logo__icon {
    width: 28px;
    height: 28px;
  }

  .workspace-topbar--mobile .topbar-actions {
    height: 36px;
    gap: 8px;
  }

  .workspace-topbar--mobile .bonus-button {
    display: none;
  }

  .workspace-topbar--mobile .upgrade-button {
    order: 2;
    width: 36px;
    height: 36px;
    padding: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
  }

  .workspace-topbar--mobile .upgrade-button__desktop-icon,
  .workspace-topbar--mobile .upgrade-button span {
    display: none;
  }

  .workspace-topbar--mobile .upgrade-button__mobile-icon {
    display: block;
  }

  .mobile-bonus-button {
    position: fixed;
    top: 94px;
    right: 0;
    z-index: 2;
    width: 104px;
    height: 36px;
    padding: 0 6px 0 10px;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    border-radius: 66px 0 0 66px;
    background: rgba(0, 0, 0, 0.8);
    color: #d38743;
    font-family: 'Source Han Sans CN VF', 'Microsoft YaHei', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: normal;
    white-space: nowrap;
  }

  .mobile-bonus-button__icon {
    width: 18px;
    height: 18px;
    display: block;
    flex-shrink: 0;
    object-fit: contain;
  }

  .workspace-topbar--mobile .credits-button {
    order: 1;
    min-width: 64px;
    height: 36px;
    padding: 0 8px;
    border-color: rgba(0, 0, 0, 0.06);
    font-family: 'PingFang SC', sans-serif;
    font-size: 13px;
    font-weight: 600;
  }

  .workspace-topbar--mobile .credits-button__buy {
    display: none;
  }

  .workspace-topbar--mobile .workspace-menu-button {
    order: 4;
    width: 36px;
    height: 36px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 66px;
    background: #ffffff;
    color: #160211;
  }

  .workspace-topbar--mobile .avatar-button {
    order: 3;
  }
</style>
